import { QuickstartTheme } from '../types/theme';

/**
 * Authored copy for the thank-you / join-failure screen, shared by the mobile
 * and desktop shells so the two cannot drift. Names come from the campaign
 * theme (`gameName`, `storePlatform`), which the engineer sets per campaign —
 * STF reads "Get Super Tiny Football on Google Play".
 *
 * Theme-driven: `ctaHeaderText`/`ctaSubheaderText`/`ctaMessageText`/
 * `thankYouCtaLabel`/`thankYouEyebrow` override the defaults when set, so the
 * admin dashboard controls the copy without code changes.
 */

/**
 * Accent for the game title and store name inside the store line,
 * and for the {game}/{store} words the thank-you copy emboldens.
 * Editable from the admin Thank You Page Colors (thankYouAccentColor);
 * #D821D4 when unset.
 */
export const THANK_YOU_ACCENT_DEFAULT = '#D821D4';

/** Resolve the thank-you store-line accent from the theme, defaulting to #D821D4. */
export const resolveThankYouAccentColor = (theme: QuickstartTheme): string =>
  theme.thankYouAccentColor?.trim() || THANK_YOU_ACCENT_DEFAULT;

export interface ThankYouCopy {
  headline: string;
  /** Plain-text form of the store line, for the copy block's string slot. */
  subcopy: string;
  ctaLabel: string;
  gameName: string;
  storePlatform: string;
  /** Accent color for the {game}/{store} words in the store line. */
  accentColor: string;
  /** Eyebrow line above the headline; null hides it. */
  eyebrow: string | null;
  /** Optional supporting line; null hides it. */
  message: string | null;
}

const DEFAULT_HEADLINE = 'Make It Yours';
const DEFAULT_EYEBROW = 'AVAILABLE NOW';

export const thankYouCopy = (theme: QuickstartTheme): ThankYouCopy => {
  const storeLine = `Get ${theme.gameName} on ${theme.storePlatform}`;
  return {
    headline: theme.ctaHeaderText?.trim() || DEFAULT_HEADLINE,
    subcopy: theme.ctaSubheaderText?.trim() || storeLine,
    ctaLabel: theme.thankYouCtaLabel?.trim() || `Download on ${theme.storePlatform}`,
    gameName: theme.gameName,
    storePlatform: theme.storePlatform,
    accentColor: resolveThankYouAccentColor(theme),
    eyebrow: resolveThankYouEyebrow(theme),
    message: theme.ctaMessageText?.trim() || null,
  };
};

/** Eyebrow label above the thank-you headline; blank is treated as hidden. */
export const resolveThankYouEyebrow = (theme: QuickstartTheme): string | null => {
  const value = theme.thankYouEyebrow ?? DEFAULT_EYEBROW;
  return value.trim().length > 0 ? value.trim() : null;
};